import type { ConsentCategories, ConsentState, UpdateConsentInput } from './types';
import { buildState } from './storage';
import { fetchPublication, loadPagePublication, publicationReference } from './publication';
import { postReleaseConsent } from './sync';

export function hasOptionalConsent(categories: ConsentCategories): boolean {
  return Object.entries(categories).some(([category, value]) => category !== 'necessary' && value === true);
}

export function deniedCategories(categories: ConsentCategories): ConsentCategories {
  return Object.fromEntries(Object.keys(categories).map((category) => [category, category === 'necessary'])) as ConsentCategories;
}

export function createReleaseConsent(options: {
  tid: string | null; apiBase: string; lang: string; releaseId?: string;
  saved: ConsentState | null; apply: (state: ConsentState) => void;
}) {
  let operation = 0;
  let activeRequest: AbortController | null = null;
  const publication = options.tid
    ? loadPagePublication(options.tid, options.apiBase, options.lang, options.releaseId)
    : Promise.reject(new Error('missing_site_id'));
  // Gli errori iniziali sono esposti alla UI; non devono diventare unhandled rejection.
  void publication.catch(() => undefined);
  const ready = publication.then(async (loaded) => {
    const saved = options.saved;
    if (!saved || !hasOptionalConsent(saved.categories) || !loaded.online || saved.registration !== 'confirmed'
        || saved.publication?.site_id !== options.tid || saved.publication.lang !== loaded.publication.lang
        || saved.publication.consent_scope_hash !== loaded.publication.consent_scope_hash) return loaded;
    if (saved.publication.release_id !== loaded.publication.release_id) {
      const previous = await fetchPublication(options.tid!, options.apiBase, options.lang, saved.publication.release_id);
      if (!previous.online || previous.publication.content_hash !== saved.publication.content_hash
          || previous.publication.consent_scope_hash !== saved.publication.consent_scope_hash) return loaded;
    } else if (saved.publication.content_hash !== loaded.publication.content_hash) return loaded;
    if (operation === 0) options.apply(saved);
    return loaded;
  });
  void ready.catch(() => undefined);

  async function update(input: UpdateConsentInput): Promise<void> {
    const currentOperation = ++operation;
    activeRequest?.abort();
    const controller = activeRequest = new AbortController();
    const requested = buildState(input.categories);
    // Revoca immediata, anche se pubblicazione/challenge/registrazione non rispondono.
    const pending: ConsentState = { ...requested, categories: deniedCategories(requested.categories), registration: 'pending' };
    options.apply(pending);
    let finalState: ConsentState = { ...pending, registration: 'unrecorded' };
    let error: string | undefined;
    try {
      const loaded = await publication;
      if (operation !== currentOperation) return;
      const ref = publicationReference(loaded.publication);
      finalState = { ...pending, publication: ref, registration: 'unrecorded' };
      if (Object.entries(requested.categories).some(([category, value]) => category !== 'necessary' && value === true && !loaded.publication.banner.categories.includes(category))) {
        throw new Error('category_outside_publication');
      }
      const result = await postReleaseConsent(options.tid!, requested, input.source ?? 'programmatic', loaded.publication, controller.signal);
      error = result.error;
      if (result.ok) finalState = { ...requested, registration: 'confirmed', publication: ref };
    } catch (failure) {
      error = failure instanceof Error ? failure.message : 'publication_unavailable';
    }
    // Una risposta tardiva non può annullare una scelta o cancellazione più recente.
    if (operation !== currentOperation) return;
    options.apply(finalState);
    window.dispatchEvent(new CustomEvent('emerge-privacy-consent-registration', {
      detail: { status: finalState.registration, error },
    }));
  }

  return { ready: publication, restored: ready, update, invalidate: () => { operation++; activeRequest?.abort(); } };
}
